import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { useSession } from '../lib/auth';
import { useUserOrg } from '../lib/org';
import {
  TIER_CATALOG,
  BillingCycle,
  startCheckout,
  annualSavingGbp,
  getBillingSnapshot,
  BillingSnapshot,
} from '../lib/billing';
import { signOut } from '../lib/auth';
import { isNativeApp } from '../lib/nativeShell';
import { IapPlans } from '../components/IapPlans';

/**
 * Paywall page. Shown when a signed-in user doesn't have an active subscription
 * (see RequireSubscription). The flow is:
 *   1. They sign up → confirm email → log in.
 *   2. RequireSubscription sees no active sub and redirects them here.
 *   3. They pick a tier + cycle, click Subscribe.
 *   4. Stripe Checkout (via existing Payment Links) — pay or back out.
 *   5. Webhook flips `subscription_status` → `active`.
 *   6. Next navigation: RequireSubscription lets them through.
 *
 * The page also surfaces "Sign out" (so a wrong account can be backed out of
 * without inspecting cookies) and a link to /settings/plan for customers who
 * have an existing subscription that has lapsed and want billing context.
 *
 * Native shells (iOS / Android Capacitor) follow the same reader-mode pattern
 * as Plan.tsx — Apple Guideline 3.1.1 forbids the in-app checkout flow, so
 * we surface a single external-link button to the web account page instead.
 */
export function Paywall() {
  const { session } = useSession();
  const { org } = useUserOrg();
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [billing, setBilling] = useState<BillingSnapshot | null>(null);
  const native = isNativeApp();

  // If we got here mid-grace or with a 'cancelled' status approaching the
  // period end, we want to tell the customer that explicitly rather than
  // a generic "you need to subscribe" — they may already think they have
  // an active sub. Best effort, silently fails if billing isn't readable.
  // Depend on org.org_id (primitive) rather than the whole org object so
  // we don't re-fire on every render of the parent — same pattern as
  // RequireSubscription, see Sentry HS-SAAS-APP-8.
  const orgId = org?.org_id ?? null;
  const navigate = useNavigate();
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const b = await getBillingSnapshot(orgId);
        if (cancelled) return;
        setBilling(b);
        // SAFETY NET: if billing actually reports as active when we got here,
        // the user landed on /paywall by mistake — probably a render-race in
        // RequireSubscription firing navigate('/paywall') before billing
        // loaded. Kick them straight back to /, where RequireSubscription will
        // now see the loaded billing and let them through. Fixes Marc's
        // "stuck on paywall despite active subscription" report 11 Jun 2026.
        if (b.status === 'active') {
          navigate('/', { replace: true });
        }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
    // navigate is intentionally not in deps — same reason as
    // RequireSubscription: useNavigate isn't stable in BrowserRouter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function onSubscribe(tier: 'tier1' | 'tier2' | 'tier3') {
    setBusy(tier);
    setError(null);
    try {
      await startCheckout(tier, cycle);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  async function onSignOut() {
    setBusy('signout');
    try {
      await signOut();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  const statusLine = (() => {
    if (!billing) return null;
    if (billing.status === 'past_due') {
      return 'Your last payment failed. Update your card to restore access.';
    }
    if (billing.status === 'cancelled') {
      return 'Your subscription has ended. Resubscribe to pick up where you left off.';
    }
    if (billing.status === 'paused') {
      return 'Your subscription is paused. Resume from a tier below to restore access.';
    }
    return null;
  })();

  return (
    <AuthLayout title="Subscribe to continue">
      <h1>Pick a plan to get started</h1>
      <p className="muted" style={{ marginBottom: 16 }}>
        Welcome{session?.user.email ? `, ${session.user.email}` : ''}. Your account is ready —
        pick a plan below to start your 30-day free trial. You add your card now, but nothing is
        taken until day 30 — cancel before then and you pay nothing.
      </p>

      {statusLine && (
        <div className="banner banner-error" style={{ marginBottom: 12 }}>{statusLine}</div>
      )}
      {error && <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>}

      {native ? (
        // NATIVE (iOS / Android) — In-App Purchase only (Guideline 3.1.1).
        <IapPlans
          userId={session?.user.id}
          onPurchased={() => navigate('/', { replace: true })}
        />
      ) : (
        <>
          <div
            role="tablist"
            aria-label="Billing cycle"
            className="rams-trade-chips"
            style={{ marginBottom: 12 }}
          >
            <button
              type="button"
              role="tab"
              aria-selected={cycle === 'monthly'}
              className={`rams-trade-chip ${cycle === 'monthly' ? 'active' : ''}`}
              onClick={() => setCycle('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={cycle === 'annual'}
              className={`rams-trade-chip ${cycle === 'annual' ? 'active' : ''}`}
              onClick={() => setCycle('annual')}
            >
              Annual <span style={{ opacity: 0.85, fontWeight: 500 }}>· 2 months free</span>
            </button>
          </div>

          {(['tier1', 'tier2', 'tier3'] as const).map((t) => {
            const cat = TIER_CATALOG[t];
            const isAnnual = cycle === 'annual';
            const headlinePerMonth = isAnnual ? Math.round(cat.yearlyGbp / 12) : cat.monthlyGbp;
            const saving = annualSavingGbp(t);
            return (
              <div key={t} className="card" style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <p style={{ margin: 0, fontWeight: 600 }}>{cat.label}</p>
                  <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                    £{headlinePerMonth}<span style={{ fontSize: 13, fontWeight: 400 }}>/month</span>
                  </p>
                </div>
                <p className="muted" style={{ margin: '4px 0 8px', fontSize: 13 }}>
                  {cat.phones} · unlimited sites, sub-contractors, sign-ins
                </p>
                {isAnnual && (
                  <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--success, #2F6B47)', fontWeight: 600 }}>
                    £{cat.yearlyGbp}/year billed once · save £{saving}
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: '100%', fontSize: 14 }}
                  disabled={busy === t}
                  onClick={() => onSubscribe(t)}
                >
                  {busy === t ? 'Opening checkout…' : `Start 30-day free trial — ${cat.label}${isAnnual ? ' annual' : ''}`}
                </button>
              </div>
            );
          })}

          <div className="card" style={{ marginTop: 8, background: 'var(--bg-soft, #FAF7F2)' }}>
            <p style={{ margin: 0, fontWeight: 600 }}>Enterprise (26+ phones)</p>
            <p className="muted" style={{ margin: '4px 0 8px', fontSize: 13 }}>
              Custom pricing for larger contractors.
            </p>
            <a
              href="mailto:hello@safeandsorted.com?subject=Enterprise%20plan%20enquiry"
              className="btn btn-ghost"
              style={{ width: '100%', textAlign: 'center', textDecoration: 'none', fontSize: 14 }}
            >
              Get in touch →
            </a>
          </div>
        </>
      )}

      {!native && (
        <p className="muted" style={{ fontSize: 12, marginTop: 18, lineHeight: 1.5 }}>
          Payments handled by Stripe. We don't see or store your card details. 30-day free trial on every plan — no charge until day 30. Cancel any time.
        </p>
      )}

      <p className="auth-footer" style={{ marginTop: 16 }}>
        <Link to="/settings/plan">Manage existing billing</Link>
        {' · '}
        <button
          type="button"
          onClick={onSignOut}
          disabled={busy === 'signout'}
          style={{ background: 'none', border: 0, color: 'var(--ink-soft)', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}
        >
          {busy === 'signout' ? 'Signing out…' : 'Sign out'}
        </button>
      </p>
    </AuthLayout>
  );
}
